import { SeatPos } from '../types/types';
import { FloorplanData } from './components/FloorPlan';
import { allocate } from './props/app/allocate';

interface Row {
    min: number;
    max: number;
}

interface SeatGroup {
    rowNumber: number;
    from: number;
    length: number;
}

type CanAssignSeat = (seat: SeatPos, ticketTypeId: string) => boolean;

interface TypeAllocation {
    numbered: SeatPos[];
    unnumbered: number;
}

// Key: ticket type ID
export type Allocation = Record<string, TypeAllocation>;

interface AllocationWithScore {
    allocation: Allocation;
    score: number;
}

export class SeatPicker {

    private seats: Map<string, SeatPos> = new Map();
    private rows: Map<number, Row> = new Map();

    public constructor(data: FloorplanData) {
        for (const grid of data.grids) {
            for (const row of grid.rows) {
                for (const seat of row.seats) {
                    if (seat) {
                        this.addSeat(row.number, seat.number);
                    }
                }
            }
        }
    }

    public allocateSeats(
        quantities: Record<string, number>, // Key is ticket type id, value is count
        unavailable: SeatPos[],
        canAssignSeat: CanAssignSeat,
    ): Allocation {
        const total = Object.values(quantities).reduce((a, b) => a + b, 0);

        if (!total) {
            return {};
        }

        const available = this.getAvailableSeats(unavailable);
        const scores = this.getScores(available.values());

        let allocation: Allocation | undefined;

        if (available.size >= total) {
            // Find all unique consecutive groups of available seats, of any length
            const rawGroups = this.findConsecutiveGroups(available);

            // Try to find an ideal allocation where all the seats are next to each other on the same row.
            allocation = this.getIdealAllocation(quantities, canAssignSeat, total, scores, rawGroups);

            if (!allocation) {
                // Did not find a fully consecutive allocation, try combining allocations per type.
                // For example, luxury seats next to each other on one row and regular seats next
                // to each other on another row.
                allocation = this.getBestCombinedAllocation(quantities, canAssignSeat, scores, rawGroups);
            }
        }

        if (!allocation) {
            // Use a fallback allocation that won't put seats next to each other and
            // may include unnumbered extra seats.
            allocation = this.getFallbackAllocation(quantities, canAssignSeat, available, scores);
        }

        return this.sortSeats(allocation);
    }

    private addSeat(rowNumber: number, seatNumber: number): void {
        this.seats.set(getSeatKey(rowNumber, seatNumber), { rowNumber, seatNumber });
        const row = this.getRow(rowNumber);

        if (seatNumber < row.min) {
            row.min = seatNumber;
        }

        if (seatNumber > row.max) {
            row.max = seatNumber;
        }
    }

    private getAvailableSeats(unavailable: SeatPos[]): Map<string, SeatPos> {
        const map = new Map(this.seats);

        for (const seat of unavailable) {
            const key = getSeatKey(seat.rowNumber, seat.seatNumber);
            map.delete(key);
        }

        return map;
    }

    private getScores(seats: Iterable<SeatPos>): Map<string, number> {
        const result = new Map<string, number>();

        const rowNumbers = [...this.rows.keys()];
        const lowestRow = Math.min(...rowNumbers);
        const highestRow = Math.max(...rowNumbers);
        const preferredRow = Math.ceil((lowestRow + highestRow) / 2);

        for (const seat of seats) {
            const key = getSeatKey(seat.rowNumber, seat.seatNumber);
            const rowScore = Math.abs(seat.rowNumber - preferredRow);
            const row = this.rows.get(seat.rowNumber);
            const middleSeatNumber = Math.ceil((row.min + row.max) / 2);
            const seatScore = rowScore + Math.abs(seat.seatNumber - middleSeatNumber);
            result.set(key, seatScore);
        }

        return result;
    }

    private findConsecutiveGroups(available: Map<string, SeatPos>): SeatGroup[] {
        const groups: SeatGroup[] = [];

        for (const [rowNumber, row] of this.rows.entries()) {
            let group: SeatGroup | null = null;

            for (let seatNumber = row.min; seatNumber <= row.max; seatNumber += 1) {
                const key = getSeatKey(rowNumber, seatNumber);

                if (!available.has(key)) {
                    // End current group if there is one
                    group = null;
                } else if (!group) {
                    // Start new group
                    group = { rowNumber, from: seatNumber, length: 1 };
                    groups.push(group);
                } else {
                    // Extend current group
                    group.length += 1;
                }
            }
        }

        return groups;
    }

    private getIdealAllocation(
        quantities: Record<string, number>,
        canAssignSeat: CanAssignSeat,
        total: number,
        scores: Map<string, number>,
        rawGroups: SeatGroup[],
    ): Allocation | undefined {
        // Filter out shorter seat groups and generate multiple groups out of larger ones.
        // For example, a group of 10 consecutive seats can be used to create
        // three overlapping groups of 8 consecutive seats (e.g. 1-8, 2-9, 3-10)
        const groups = this.generateGroupsOfLength(rawGroups, total);

        // Find a valid allocation of the requested ticket type quantities for each group
        // where one exists.
        // For example, 8 consecutive seats that allow 5 regular tickets and 3 child tickets.
        const allocations = this.getConsecutiveAllocations(quantities, canAssignSeat, groups);

        if (allocations.length) {
            // Find allocation with best (lowest) score.
            // Sort by score in ascending order and return first element.
            const [{ allocation }] = this.addScores(allocations, scores).sort(compareAllocationsWithScore);

            return allocation;
        }
    }

    private getBestCombinedAllocation(
        quantities: Record<string, number>,
        canAssignSeat: CanAssignSeat,
        scores: Map<string, number>,
        rawGroups: SeatGroup[],
    ): Allocation | undefined {
        // TODO remove?
        // Sort ticket types by requested count, largest first
        // const sortedQuantities = Object.entries(quantities)
        //     .sort(([, count1], [, count2]) => count2 - count1);

        const allocationsByType: Record<string, AllocationWithScore[]> = {};

        for (const [ticketTypeId, count] of Object.entries(quantities)) {
            // Similar process to getIdealAllocation, but for only one ticket type
            const groups = this.generateGroupsOfLength(rawGroups, count);
            const typeQuantities = { [ticketTypeId]: quantities[ticketTypeId] };
            const allocations = this.getConsecutiveAllocations(typeQuantities, canAssignSeat, groups);

            // Calculate a score for each allocation and store it for combining with other ticket types
            allocationsByType[ticketTypeId] = this.addScores(allocations, scores);
        }

        // Generate all possible allocation combinations.
        // E.g. first allocation of first ticket type + first allocation of second ticket type,
        // then first allocation of first ticket type + second allocation of second ticket type, etc.
        // These may contain overlapping seats.
        const combined = this.combineAllocations(allocationsByType);

        // Sort combinations by combined score (best/lowest first)
        combined.sort(compareAllocationsWithScore);

        // We can't pick the best one immediately as it may contain overlapping seats.
        // Instead we iterate until we find the best non-overlapping allocation.
        for (const { allocation } of combined) {
            if (this.allocationOverlaps(allocation)) {
                continue;
            }

            return allocation;
        }

        return;
    }

    private getFallbackAllocation(
        quantities: Record<string, number>,
        canAssignSeat: CanAssignSeat,
        available: Map<string, SeatPos>,
        scores: Map<string, number>,
    ): Allocation {
        const allocation: Allocation = {};

        // Sort all available seats by score
        const sortedSeats = [...available].sort(
            ([key1], [key2]) => scores.get(key1) - scores.get(key2),
        ).map(([, seat]) => seat);

        const used = new Set<SeatPos>();

        // Try to assign best seats to the first ticket type, second best to the next, etc.
        // Probably not the most optimal solution, but good enough for a fallback.
        for (const [ticketTypeId, count] of Object.entries(quantities)) {
            if (!count) {
                continue;
            }

            const seats: SeatPos[] = [];

            // Find as many matching numbered seats as possible
            for (const seat of sortedSeats) {
                if (used.has(seat)) {
                    // The seat is already used by an earlier ticket type
                    continue;
                }

                if (canAssignSeat(seat, ticketTypeId)) {
                    seats.push(seat);
                    used.add(seat);

                    if (seats.length === count) {
                        break;
                    }
                }
            }

            allocation[ticketTypeId] = {
                numbered: seats,
                unnumbered: count - seats.length,
            };
        }

        return allocation;
    }

    private sortSeats(allocation: Allocation): Allocation {
        for (const typeAllocation of Object.values(allocation)) {
            typeAllocation.numbered.sort(compareSeats);
        }

        return allocation;
    }

    private getRow(rowNumber: number): Row {
        let row: Row = this.rows.get(rowNumber);

        if (!row) {
            row = { min: Infinity, max: 0 };
            this.rows.set(rowNumber, row);
        }

        return row;
    }

    private generateGroupsOfLength(inputGroups: SeatGroup[], length: number): SeatGroup[] {
        const outputGroups: SeatGroup[] = [];

        for (const { rowNumber, from, length: inputLength } of inputGroups) {
            // If input group is shorter than length, the loop won't run
            for (let i = 0; i <= inputLength - length; i += 1) {
                outputGroups.push({ rowNumber, from: from + i, length });
            }
        }

        return outputGroups;
    }

    private getConsecutiveAllocations(
        quantities: Record<string, number>,
        canAssignSeat: CanAssignSeat,
        groups: SeatGroup[],
    ): Allocation[] {
        return groups
            .map((group) => {
                const seats = this.getSeatsFromGroup(group);
                const rawAllocation = allocate(seats, quantities, canAssignSeat);

                if (rawAllocation) {
                    const allocation: Allocation = {};

                    for (const [ticketTypeId, typeSeats] of Object.entries(rawAllocation)) {
                        allocation[ticketTypeId] = {
                            numbered: typeSeats,
                            unnumbered: 0,
                        };
                    }

                    return allocation;
                }
            })
            .filter((result) => result); // Remove undefined
    }

    private addScores(allocations: Allocation[], scores: Map<string, number>): AllocationWithScore[] {
        return allocations.map((allocation): AllocationWithScore => {
            const score = this.getAllocationScore(allocation, scores);

            return { allocation, score };
        });
    }

    // Combine allocations (with score) recursively
    private combineAllocations(
        allocationsByType: Record<string, AllocationWithScore[]>,
        index: number = 0,
        partialResult: AllocationWithScore = { allocation: {}, score: 0 },
        result: AllocationWithScore[] = [],
    ): AllocationWithScore[] {
        const ticketTypeIds = Object.keys(allocationsByType);
        const ticketTypeId = ticketTypeIds[index];

        for (const { allocation, score } of allocationsByType[ticketTypeId]) {
            const combined: AllocationWithScore = {
                allocation: { ...partialResult.allocation, ...allocation },
                score: partialResult.score + score,
            };

            if (index < ticketTypeIds.length - 1) {
                // We have more ticket types to process, recurse
                this.combineAllocations(allocationsByType, index + 1, combined, result);
            } else {
                // We're processing the last ticket type, populate the result
                result.push(combined);
            }
        }

        return result;
    }

    private allocationOverlaps(allocation: Allocation): boolean {
        const keys = new Set<string>();

        for (const key of this.iterateSeatKeys(allocation)) {
            if (keys.has(key)) {
                return true;
            }

            keys.add(key);
        }

        return false;
    }

    private getSeatsFromGroup({ rowNumber, from, length }: SeatGroup): SeatPos[] {
        const seats: SeatPos[] = [];

        for (let i = 0; i < length; i += 1) {
            seats.push({ rowNumber, seatNumber: from + i });
        }

        return seats;
    }

    private getAllocationScore(allocation: Allocation, scores: Map<string, number>): number {
        let score = 0;

        for (const key of this.iterateSeatKeys(allocation)) {
            score += scores.get(key);
        }

        return score;
    }

    private *iterateSeatKeys(allocation: Allocation): Generator<string> {
        for (const typeAllocation of Object.values(allocation)) {
            for (const seat of typeAllocation.numbered) {
                yield getSeatKey(seat.rowNumber, seat.seatNumber);
            }
        }
    }

}

export const getSeatKey = (rowNumber: number, seatNumber: number): string => `${rowNumber},${seatNumber}`;

const compareSeats = (seat1: SeatPos, seat2: SeatPos): number => seat1.rowNumber - seat2.rowNumber || seat1.seatNumber - seat2.seatNumber;

const compareAllocationsWithScore = (
    withScore1: AllocationWithScore,
    withScore2: AllocationWithScore,
): number => withScore1.score - withScore2.score;
